import React from 'react';
import { Link } from 'react-router-dom';

const Mail: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <a href="mailto:contact@i-think-its-you.at">{children}</a>
);

export const TermsEnglish: React.FC = () => (
  <>
    <p>
      <em>As of: May 2026</em>
    </p>

    <h2>1. Provider, scope, contact points</h2>
    <p>
      <strong>1.1</strong> These terms of use govern use of the website, mobile applications,
      and other digital services of I Think It´s You, acting as I Think It´s You and Event Partner
      Austria Verein (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;), insofar as they refer to
      these terms.
    </p>
    <p>
      <strong>1.2</strong> The provider is:
      <br />
      Event Partner Austria Verein
      <br />
      Kwizdastrasse 9/11
      <br />
      2100 Korneuburg
      <br />
      Austria
      <br />
      Email: <Mail>contact@i-think-its-you.at</Mail>
      <br />
      Contact form: <Link to="/contact">Contact form</Link>
      <br />
      Legal notice (Impressum): <Link to="/imprint">Imprint</Link>
    </p>
    <p>
      <strong>1.3</strong> User contact point under the Digital Services Act (DSA):
      email/form: <Mail>contact@i-think-its-you.at</Mail>
    </p>
    <p>
      <strong>1.4</strong> Authority contact point under the DSA:{' '}
      <Mail>contact@i-think-its-you.at</Mail>
    </p>
    <p>
      <strong>1.5</strong> Legal representative in the European Union: if we have no
      establishment in the EU, we will designate a legal representative in a member state:
      [name/company, address, email]
    </p>
    <p>
      <strong>1.6</strong> These terms may be saved and printed. The contract language is German.
      If we provide a translation, the German version prevails in case of discrepancies.
    </p>

    <h2>2. Description of the service and formation of the user contract</h2>
    <p>
      <strong>2.1</strong> I Think It´s You is an online service through which adult users can
      describe, publish, and search for missed encounters (&quot;missed connections&quot;) and —
      where offered — initiate contact via the platform.
    </p>
    <p>
      <strong>2.2</strong> The user contract is formed when you accept these terms and we
      activate your account or enable use that requires registration. For purely public areas,
      these terms apply from the moment you start using the respective function.
    </p>
    <p>
      <strong>2.3</strong> Where individual features or additional services are subject to
      separate terms, we will inform you separately before you use them.
    </p>
    <p>
      <strong>2.4</strong> There is no right to permanent availability of specific features.
      Section 15 remains unaffected.
    </p>

    <h2>3. Eligibility</h2>
    <p>
      <strong>3.1</strong> You may use our service only if you are at least 18 years old and
      entitled under applicable law to use digital services.
    </p>
    <p>
      <strong>3.2</strong> Use by persons under 18 is prohibited.
    </p>
    <p>
      <strong>3.3</strong> You may create only one account for yourself unless we expressly
      permit multiple accounts.
    </p>

    <h2>4. Registration, account, and access security</h2>
    <p>
      <strong>4.1</strong> On registration you must provide accurate, current, and complete
      information and update it when it changes.
    </p>
    <p>
      <strong>4.2</strong> You must keep your login credentials confidential and protect them
      against unauthorized access.
    </p>
    <p>
      <strong>4.3</strong> You must inform us without delay if you suspect unauthorized use of
      your account.
    </p>
    <p>
      <strong>4.4</strong> User names, profile text, or other identifiers must not infringe third
      party rights, impersonate anyone, or be insulting, discriminatory, or misleading. We may
      object to, restrict, or adjust such information in accordance with these terms.
    </p>

    <h2>5. Our role as a platform</h2>
    <p>
      <strong>5.1</strong> We provide the technical platform for user contributions and contact
      initiation. We are not a matchmaking or dating agency, we do not guarantee reciprocity or
      success, and we are generally not a party to personal contact, meetings, or other
      arrangements between users.
    </p>
    <p>
      <strong>5.2</strong> We do not conduct general identity, background, or safety checks
      unless we say so expressly.
    </p>
    <p>
      <strong>5.3</strong> Meetings, messages, outreach, or other interaction with others is at
      your own risk. Please act carefully and respect other people&apos;s boundaries.
    </p>

    <h2>6. Public visibility and privacy</h2>
    <p>
      <strong>6.1</strong> Posts in public areas of the service may be visible to other users.
      Unless technically prevented, public content may also be viewed, linked, or indexed outside
      the platform.
    </p>
    <p>
      <strong>6.2</strong> Publish only content you are allowed and willing to share publicly.
    </p>
    <p>
      <strong>6.3</strong> Describe missed encounters so the situation is understandable without
      unnecessarily intruding on others&apos; privacy.
    </p>
    <p>
      <strong>6.4</strong> Permitted information includes restrained context such as approximate
      public places, a time window, and outwardly visible circumstances, where proportionate.
      Not permitted in particular are exact home addresses, private contact details, or other
      information that makes a person immediately identifiable or locatable.
    </p>

    <h2>7. Rules of conduct and prohibited content</h2>
    <p>
      <strong>7.1</strong> You may use our service only lawfully, fairly, and in line with these
      terms.
    </p>
    <p>
      <strong>7.2</strong> In particular, you must not:
    </p>
    <ol type="a">
      <li>
        publish personal data of third parties that is not needed to describe the encounter;
      </li>
      <li>
        publish exact home or work addresses, phone numbers, email addresses, ID or bank
        details, license plates, booking data, social media handles, or other directly
        identifying information about third parties without their express consent;
      </li>
      <li>
        publish photos, videos, audio, screenshots, or other depictions of identifiable persons
        without their consent;
      </li>
      <li>post content about minors or deliberately contact minors;</li>
      <li>
        disclose special categories of third party personal data, in particular relating to
        health, ethnic origin, religion, political views, biometric data, sex life, or sexual
        orientation;
      </li>
      <li>
        harass, stalk, intimidate, sexually coerce, pressure, or repeatedly contact someone
        unwanted;
      </li>
      <li>dox or try to reveal someone&apos;s identity against their will;</li>
      <li>
        spread defamatory or otherwise unproven allegations against identifiable persons,
        in particular claims of crimes or similarly serious accusations;
      </li>
      <li>publish hate speech, discriminatory content, calls to violence, or threats;</li>
      <li>publish pornographic, sexually explicit, or gravely offensive content;</li>
      <li>impersonate another person or run a misleading profile;</li>
      <li>
        send spam, mass contacts, ads, commercial solicitations, chain letters, or other
        unsolicited communication;
      </li>
      <li>use malware, harmful code, or other technical attacks;</li>
      <li>
        abuse the service in an automated way, in particular through scraping, bots, data mining,
        or similar methods, except where strictly required by law;
      </li>
      <li>circumvent our security measures or disrupt the operation of the service;</li>
      <li>abuse location features to track, monitor, or pressure people.</li>
    </ol>
    <p>
      <strong>7.3</strong> Where we offer direct messages or other contact features, you must
      respect a refusal, no reply, block, or other clear unwillingness to be contacted.
    </p>

    <h2>8. User contributions, responsibility, and rights</h2>
    <p>
      <strong>8.1</strong> &quot;Contributions&quot; are all content you upload, transmit, store,
      or otherwise make available in connection with the service, including text, images,
      profile data, messages, and comments.
    </p>
    <p>
      <strong>8.2</strong> You remain the owner of your contributions. We do not claim ownership
      of your content.
    </p>
    <p>
      <strong>8.3</strong> You warrant that you have all rights, consents, and authority needed
      to post your contributions lawfully and to grant us the usage rights described in this
      section.
    </p>
    <p>
      <strong>8.4</strong> You grant us a non-exclusive, worldwide, royalty-free,
      sublicensable license in your contributions to the extent and for as long as necessary to
      operate and provide the service. This includes, in particular, hosting, storage, technical
      reproduction, formatting, display, making available to the public within the service,
      transmission, moderation, backup, and use for abuse prevention, security, error analysis,
      and necessary further development of the service.
    </p>
    <p>
      <strong>8.5</strong> This license ends when your contribution is deleted or your account
      ends, subject to:
    </p>
    <ul>
      <li>backups and technical caches,</li>
      <li>legal retention duties,</li>
      <li>
        continued storage where necessary for legal defense, abuse prevention, or enforcement of
        these terms.
      </li>
    </ul>
    <p>
      <strong>8.6</strong> We do not use your contributions for advertising outside the service
      unless you have given separate consent.
    </p>
    <p>
      <strong>8.7</strong> Where technical processing is required, you agree to formatting and
      adaptation. This does not constitute a general assignment or blanket waiver of moral
      rights.
    </p>

    <h2>9. Notice of illegal or rule-breaking content</h2>
    <p>
      <strong>9.1</strong> We provide an easily accessible process to report allegedly unlawful
      content or content that breaches these terms: the <Link to="/contact">contact form</Link>{' '}
      and the in-app reporting function on posts and comments.
    </p>
    <p>
      <strong>9.2</strong> A notice should where possible include:
    </p>
    <ul>
      <li>exact identification of the content (e.g. link, post ID, user name),</li>
      <li>a description of the issue,</li>
      <li>the reasons you consider the content unlawful or in breach of the rules,</li>
      <li>documents or screenshots if applicable,</li>
      <li>your contact details for follow-up questions.</li>
    </ul>
    <p>
      <strong>9.3</strong> We review notices carefully and take appropriate measures using due
      discretion. Depending on the case, we may remove content, restrict access, reduce
      visibility, limit contact features, warn users, or suspend accounts temporarily or
      permanently.
    </p>
    <p>
      <strong>9.4</strong> Where required or appropriate by law, we inform affected parties of our
      decision and the main reasons.
    </p>
    <p>
      <strong>9.5</strong> We may prioritize notices from trusted reporters and notices in acute
      safety situations.
    </p>
    <p>
      <strong>9.6</strong> We are not obliged to proactively monitor all content. We may however
      use technical and manual measures to detect and limit abuse, security risks, and breaches of
      these terms.
    </p>

    <h2>10. Internal complaints system</h2>
    <p>
      <strong>10.1</strong> If we take action against your content, account, or use of the
      service, or if you filed a notice and disagree with the decision on it, you may lodge a
      complaint through our internal complaints system within six months of being informed of the
      decision: <Link to="/contact">complaints form (contact)</Link>.
    </p>
    <p>
      <strong>10.2</strong> The complaints process is available electronically and free of
      charge.
    </p>
    <p>
      <strong>10.3</strong> We handle complaints promptly, carefully, without discrimination, and
      without arbitrariness. Complaint decisions are not taken by automated decision-making alone.
    </p>
    <p>
      <strong>10.4</strong> If your complaint is justified, we will without undue delay reverse
      or appropriately adjust the decision that was challenged.
    </p>
    <p>
      <strong>10.5</strong> You will receive a reasoned decision. Statutory rights to go to court
      or to use out-of-court dispute bodies remain unaffected.
    </p>

    <h2>11. Ranking, sorting, and recommendation of content</h2>
    <p>
      <strong>11.1</strong> Where we sort, prioritize, or recommend content, we use the main
      parameters described in the service.
    </p>
    <p>
      <strong>11.2</strong> The main parameters currently used for sorting or recommending public
      posts include in particular:
    </p>
    <ul>
      <li>[selected location / search radius],</li>
      <li>[recency of the post],</li>
      <li>[text relevance to search location or query],</li>
      <li>[language / region],</li>
      <li>[completeness of the post],</li>
      <li>[safety, quality, or moderation signals],</li>
      <li>[interaction signals, where actually used].</li>
    </ul>
    <p>
      <strong>11.3</strong> Where technically possible and sensible, we also provide a
      non-personalized or less personalized sort, e.g. by location or recency.
    </p>

    <h2>12. Rights in the service and our marks</h2>
    <p>
      <strong>12.1</strong> The service itself, including software, design, databases, logos,
      brands, texts, and other elements, remains our property or that of our licensors.
    </p>
    <p>
      <strong>12.2</strong> For the duration of the user contract we grant you a simple,
      non-transferable, revocable right to use the service for private or other expressly
      permitted purposes under these terms.
    </p>
    <p>
      <strong>12.3</strong> Not permitted in particular are:
    </p>
    <ul>
      <li>copying, selling, or commercially exploiting substantial parts of the service,</li>
      <li>abusive automated extraction,</li>
      <li>reverse engineering except where strictly required by law,</li>
      <li>using our marks without our prior consent.</li>
    </ul>

    <h2>13. Third-party services</h2>
    <p>
      <strong>13.1</strong> The service may embed features or content from third parties, such as
      social login, maps, hosting, payment, or analytics.
    </p>
    <p>
      <strong>13.2</strong> Third-party services are additionally governed by their own terms and
      privacy policies. We are not responsible for third-party sites, apps, or content except
      where the law says otherwise.
    </p>

    <h2>14. Free use, donations, and future paid features</h2>
    <p>
      <strong>14.1</strong> Unless stated otherwise, use of the core service is free of charge.
    </p>
    <p>
      <strong>14.2</strong> Voluntary donations do not create any right to specific features,
      preferred treatment, particular availability, or publication of individual posts.
    </p>
    <p>
      <strong>14.3</strong> Donations or other payments may be processed via third parties; the
      payment provider&apos;s additional terms may then apply.
    </p>
    <p>
      <strong>14.4</strong> If we introduce paid additional services in future, we will inform
      you clearly before contract conclusion about price, scope, duration, termination, and any
      statutory withdrawal rights.
    </p>

    <h2>15. Availability, maintenance, and changes to the service</h2>
    <p>
      <strong>15.1</strong> We strive for reasonable availability of the service. We do not owe
      uninterrupted or error-free availability.
    </p>
    <p>
      <strong>15.2</strong> We may temporarily restrict or interrupt the service where necessary
      for maintenance, security, abuse prevention, technical improvements, or legal reasons.
    </p>
    <p>
      <strong>15.3</strong> We may change, develop, or discontinue features where there is a
      factual reason, in particular security requirements, legal changes, abuse, technical shifts,
      or legitimate further development of the offering.
    </p>
    <p>
      <strong>15.4</strong> If changes materially disadvantage agreed paid or ongoing services,
      your mandatory statutory rights remain unaffected.
    </p>

    <h2>16. Consumer information and statutory rights</h2>
    <p>
      <strong>16.1</strong> If you are a consumer, your mandatory statutory rights remain
      unaffected, in particular regarding digital services, warranty, ending the contract,
      damages, data protection, and access to courts.
    </p>
    <p>
      <strong>16.2</strong> Where consumer law requires pre-contract information or a right of
      withdrawal, we provide that information separately in the legally required form before
      conclusion of the contract.
    </p>
    <p>
      <strong>16.3</strong> Where a statutory withdrawal right exists and you ask for performance
      to begin before the withdrawal period ends, we obtain — where legally required — your
      separate consent and any required confirmations.
    </p>

    <h2>17. Privacy and cookies</h2>
    <p>
      <strong>17.1</strong> Processing of personal data is governed by our privacy policy:{' '}
      <Link to="/privacy">privacy policy</Link>.
    </p>
    <p>
      <strong>17.2</strong> Information on cookies and similar technologies is in our cookie
      notice / privacy policy: <Link to="/privacy">privacy &amp; cookies</Link>.
    </p>
    <p>
      <strong>17.3</strong> These terms do not exhaustively cover all data protection questions.
      The privacy policy and applicable data protection law govern in that respect.
    </p>

    <h2>18. Termination by you</h2>
    <p>
      <strong>18.1</strong> You may delete your account or request deletion at any time with
      effect for the future.
    </p>
    <p>
      <strong>18.2</strong> After termination or deletion we will deactivate or delete your
      account unless legal retention duties, legitimate security interests, abuse prevention, or
      pending litigation prevent it.
    </p>
    <p>
      <strong>18.3</strong> Public posts may — where the product allows — be deleted by you.
      Existing backups, logs, or legally required retention remain unaffected.
    </p>

    <h2>19. Suspension and termination by us</h2>
    <p>
      <strong>19.1</strong> We may terminate the user contract for a free account on 30
      days&apos; ordinary notice.
    </p>
    <p>
      <strong>19.2</strong> We may temporarily suspend access to the service in whole or in part
      or terminate the contract without notice for good cause if, taking all circumstances and
      both sides&apos; interests into account, continuation would be unreasonable.
    </p>
    <p>
      <strong>19.3</strong> Good cause includes in particular if you:
    </p>
    <ul>
      <li>materially or repeatedly breach these terms,</li>
      <li>post unlawful content,</li>
      <li>endanger, harass, or deceive others,</li>
      <li>technically abuse the service,</li>
      <li>provide false information on material points,</li>
      <li>circumvent security mechanisms, or</li>
      <li>breach legal duties.</li>
    </ul>
    <p>
      <strong>19.4</strong> Where the law requires or it is appropriate, we will warn you before
      suspension or termination and give you a chance to respond, unless immediate action is needed
      to prevent danger, meet legal duties, or avoid further harm.
    </p>
    <p>
      <strong>19.5</strong> After suspension or termination you may not create a new account
      unless we expressly allow it.
    </p>

    <h2>20. Liability</h2>
    <p>
      <strong>20.1</strong> We are liable without limitation under the law:
    </p>
    <ul>
      <li>for intent and gross negligence,</li>
      <li>for injury to life, body, or health,</li>
      <li>under product liability law,</li>
      <li>for fraudulent concealment of defects, and</li>
      <li>in all cases of mandatory statutory liability.</li>
    </ul>
    <p>
      <strong>20.2</strong> For simple negligence we are liable only for breach of duties whose
      fulfilment is essential for proper performance of the contract and on whose compliance you
      may regularly rely. In that case our liability is limited to foreseeable, typical
      contract damage.
    </p>
    <p>
      <strong>20.3</strong> For content, statements, or conduct of users or other third parties
      we are liable only as provided by law.
    </p>
    <p>
      <strong>20.4</strong> The above liability rules also benefit our legal representatives,
      employees, and vicarious agents.
    </p>

    <h2>21. Changes to these terms</h2>
    <p>
      <strong>21.1</strong> We may change these terms if there is a factual reason, in particular:
    </p>
    <ul>
      <li>changes in the law,</li>
      <li>new regulatory requirements,</li>
      <li>security or abuse situations,</li>
      <li>technical changes to the service, or</li>
      <li>expansion or restructuring of our offering.</li>
    </ul>
    <p>
      <strong>21.2</strong> Purely editorial changes, clarifications, contact updates, or changes
      that exclusively benefit users may take effect for the future without separate consent.
    </p>
    <p>
      <strong>21.3</strong> We will notify you of other material changes in good time before they
      take effect, in a clear form.
    </p>
    <p>
      <strong>21.4</strong> Changes that materially disadvantage you take effect only if:
    </p>
    <ul>
      <li>we have a factual reason,</li>
      <li>we inform you in good time of the content, timing, and significance of the change, and</li>
      <li>
        you consent or continued use after a clear notice may count as consent, without mandatory
        consumer law to the contrary.
      </li>
    </ul>
    <p>
      <strong>21.5</strong> If you do not agree with a material change, you may terminate the user
      contract free of charge until the change takes effect.
    </p>

    <h2>22. Applicable law and venue</h2>
    <p>
      <strong>22.1</strong> These terms and all disputes arising from or in connection with them
      are governed by Austrian law, excluding conflict-of-law rules and the UN Sales Convention.
    </p>
    <p>
      <strong>22.2</strong> If you are a consumer habitually resident in a state whose mandatory
      consumer protection rules give you greater protection, those mandatory rules remain
      unaffected.
    </p>
    <p>
      <strong>22.3</strong> Suits against consumers are governed by statutory venue rules.
    </p>
    <p>
      <strong>22.4</strong> If you are a business, a legal entity under public law, or a special
      fund under public law, then — where legally permissible — Vienna, Innere Stadt, is the
      exclusive place of jurisdiction.
    </p>

    <h2>23. Final provisions</h2>
    <p>
      <strong>23.1</strong> Should individual provisions of these terms be or become wholly or
      partly invalid, the validity of the remaining provisions remains unaffected.
    </p>
    <p>
      <strong>23.2</strong> An invalid provision is replaced by the statutory rule. The same
      applies to unintended gaps.
    </p>
    <p>
      <strong>23.3</strong> Our privacy policy, cookie notice, and any supplementary community
      rules or special service conditions in their respective current version supplement these
      terms.
    </p>

    <h2>24. Contact</h2>
    <p>For questions, notices, complaints, or legal matters you can reach us at:</p>
    <p>
      Event Partner Austria Verein
      <br />
      Kwizdastrasse 9/11
      <br />
      2100 Korneuburg
      <br />
      Austria
    </p>
    <p>
      Email: <Mail>contact@i-think-its-you.at</Mail>
      <br />
      DSA user contact: <Mail>contact@i-think-its-you.at</Mail>
      <br />
      DSA authority contact: <Mail>contact@i-think-its-you.at</Mail>
      <br />
      Contact form: <Link to="/contact">Contact form</Link>
    </p>
  </>
);
